import { CAR_QUOTE_STEP_KEYS } from "@/D2C/features/car-quote/common/static";
import { saveCarQuoteStepFor } from "@/D2C/features/car-quote/redux/car-quote/carQuoteSlice";
import { createCarSession, updateCarSessionStep } from "@/D2C/features/car-quote/services/service";
import {
  buildCarCreateSessionPayload,
  buildCarDriverDetailsPayload,
  buildCarPersonalDetailsPayload,
  buildCarVehicleDetailsPayload,
} from "@/D2C/features/car-quote/utils/carQuotePayloadsUtils";
import { normalizeCurrencyInput } from "@/D2C/features/car-quote/utils/motorSumInsured";
import { normalizeMotorQuote } from "@/D2C/features/car-quote/utils/normalizeMotorQuote";
import { App_Url_New, LOB_IDS, Roles } from "@/shared/common/static";
import { SESSION_STEP_KEYS_BY_JOURNEY } from "@/shared/constants/sessionStepKeys";
import { setSession } from "@/shared/redux/reducers/authSlice";
import { clearQuoteList, setQuoteList, sortQuotes } from "@/shared/redux/reducers/commonSlice";
import { setSharedPersonalDetails } from "@/shared/redux/reducers/personalDetailsSlice";
import type { AppDispatch } from "@/shared/redux/store/store";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { streamQuotes } from "@/shared/services/quoteStreamService";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  getSessionIds,
  normalizeBoolean,
  toNullableNumber,
  type SessionIds,
} from "../utils/getSessionIds";
import type { useQuoteRefs } from "./useQuoteRefs";

type Refs = ReturnType<typeof useQuoteRefs>;

export const useMotorQuotes = (
  refs: Pick<Refs, "carPersonalRef" | "carVehicleRef" | "carDriverRef">,
  setActiveSessionIds: (ids: SessionIds) => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const { auth } = usePosterReducers();
  const isAgent = auth?.user?.user?.roles?.some(
    (role) => role.name?.trim().toLowerCase() === Roles.AGENT
  );
  const assignedUserId = isAgent ? (auth?.user?.user?.id ?? auth?.user?.id ?? null) : null;

  return useCallback(async () => {
    const personalValid = await refs.carPersonalRef.current?.submit();
    const vehicleValid = await refs.carVehicleRef.current?.submit();
    const driverValid = await refs.carDriverRef.current?.submit();
    if (!personalValid || !vehicleValid || !driverValid) return false;

    const personalData = refs.carPersonalRef.current!.getValues();
    const vehicleData = refs.carVehicleRef.current!.getValues();
    const driverData = refs.carDriverRef.current!.getValues();

    const responseData = await createCarSession({
      step: SESSION_STEP_KEYS_BY_JOURNEY.MOTOR.PERSONAL_DETAILS,
      ...buildCarCreateSessionPayload(personalData),
      assigned_user_id: assignedUserId,
      assign_user_id: assignedUserId,
    });
    const ids = getSessionIds(responseData);
    setActiveSessionIds(ids);

    dispatch(setSession(responseData));
    dispatch(setSharedPersonalDetails(personalData as any));
    dispatch(saveCarQuoteStepFor(CAR_QUOTE_STEP_KEYS.PERSONAL_DETAILS, personalData));
    dispatch(saveCarQuoteStepFor(CAR_QUOTE_STEP_KEYS.VEHICLE_DETAILS, vehicleData));
    dispatch(saveCarQuoteStepFor(CAR_QUOTE_STEP_KEYS.DRIVER_DETAILS, driverData));

    const hasActivePolicy = normalizeBoolean(driverData.hasActivePolicy);
    const claimsLastYear = normalizeBoolean(driverData.claimsLastYear);
    const gccSpec = normalizeBoolean(vehicleData.gccSpec);
    const isNewCar = normalizeBoolean(vehicleData.isNewCar);

    const requestBody = {
      personal_details: {
        first_name: personalData.firstName || "",
        last_name: personalData.lastName || "",
        mobile_no: personalData.mobileNumber?.number || "",
        email_id: personalData.email || "",
      },
      drivers: {
        nationality: driverData.nationality || "",
        dob: driverData.dateOfBirth || "",
        gender: driverData.gender === "male" ? "M" : driverData.gender === "female" ? "F" : "",
        local_license: driverData.uaeDrivingExperience || "",
        other_license: driverData.outsideUaeDrivingExperience || "",
        active_car_policy: hasActivePolicy ? "Yes" : "No",
        current_policy_name: hasActivePolicy
          ? (driverData.currentPolicyType || "").replace(/\s+/g, "")
          : "",
        current_insurer: hasActivePolicy ? driverData.currentInsurer || "" : "",
        any_claim_last_year: claimsLastYear ? "Yes" : "No",
        ...(!claimsLastYear
          ? { no_claims_certificate: driverData.no_claims_certificate || "" }
          : {}),
      },
      vehicle: {
        vehicle_name: vehicleData.carModel || "",
        model_year: vehicleData.modelYear || "",
        is_modified: gccSpec ? "No" : "Yes",
        new_car: isNewCar ? "Yes" : "No",
        place_of_regn: vehicleData.registeredEmirate || "",
        date_of_regn: vehicleData.registrationDate || "",
        registration_type: vehicleData.registeredUnder || "",
        premium_amount: normalizeCurrencyInput(vehicleData.carValue) || "",
      },
      session_id: toNullableNumber(ids.sessionId),
      client_id: toNullableNumber(ids.clientId),
      lob_id: LOB_IDS.MOTOR,
    };

    if (ids.clientId && ids.sessionId) {
      await updateCarSessionStep({
        step: SESSION_STEP_KEYS_BY_JOURNEY.MOTOR.PRE_QUOTE_DETAILS,
        payload: {
          vehicle_details: buildCarVehicleDetailsPayload(vehicleData),
          personal_details: buildCarPersonalDetailsPayload(personalData),
        },
        clientId: ids.clientId,
        sessionId: ids.sessionId,
        lobId: LOB_IDS.MOTOR,
      });
      await updateCarSessionStep({
        step: SESSION_STEP_KEYS_BY_JOURNEY.MOTOR.DRIVER_DETAILS,
        payload: {
          driver_details: buildCarDriverDetailsPayload(driverData),
          vehicle_details: buildCarVehicleDetailsPayload(vehicleData),
          personal_details: buildCarPersonalDetailsPayload(personalData),
        },
        clientId: ids.clientId,
        sessionId: ids.sessionId,
        lobId: LOB_IDS.MOTOR,
      });
    }

    const collectedQuotes: any[] = [];
    dispatch(clearQuoteList());
    await streamQuotes({
      endpointPath: App_Url_New.ApiEndPoint.motorGetQuote,
      requestBody,
      onQuoteReceived: (quote) => {
        const normalized = normalizeMotorQuote(quote as any);
        dispatch(setQuoteList(normalized));
        dispatch(sortQuotes("sequence-asc"));
        if ((quote as any)?.insurer?.name && (quote as any)?.plan?.name)
          collectedQuotes.push(quote);
      },
      onStreamComplete: () => {
        if (!ids.clientId || !ids.sessionId || collectedQuotes.length === 0) return;
        void updateCarSessionStep({
          step: "quote_request",
          payload: {
            personal_details: requestBody.personal_details,
            vehicle_details: requestBody.vehicle,
            driver_details: requestBody.drivers,
            quotes: collectedQuotes,
          },
          clientId: ids.clientId,
          sessionId: ids.sessionId,
          lobId: LOB_IDS.MOTOR,
        });
      },
      onStreamError: (err) => console.error("Motor quote stream error:", err),
    });
    dispatch(sortQuotes("sequence-asc"));
    return true;
  }, [assignedUserId, dispatch, refs, setActiveSessionIds]);
};
